import * as RxDialog from '@radix-ui/react-dialog';
import debounce from 'lodash.debounce';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';

import getBrowseSearchSuggestions from '@cartwhl/common/api/getBrowseSearchSuggestions';
import {
  CategoryAggregation,
  SubcategoryInfo,
  TagInfo,
} from '@cartwhl/common/types';
import { getResourcesUrl } from '@cartwhl/common/utils';
import Checkbox from '@cartwhl/ui/atoms/Checkbox';
import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import Input from '@cartwhl/ui/atoms/Input';
import Text from '@cartwhl/ui/base/Text';
import ChevronDropdownIcon from '@cartwhl/ui/icons/ChevronDropdownIcon';
import ChevronLeftIcon from '@cartwhl/ui/icons/ChevronLeftIcon';
import ChevronRightIcon from '@cartwhl/ui/icons/ChevronRightIcon';
import SearchIcon from '@cartwhl/ui/icons/SearchIcon';
import SortIcon from '@cartwhl/ui/icons/SortIcon';
import Button from '@cartwhl/ui/input/Button';

import useBreakpoint from 'hooks/useBreakpoint';
import useTruncateText from 'hooks/useTruncateText';
import { browseMotionStore, userStore } from 'stores';
import {
  IMAGE_PATH_BY_CATEGORY,
  MOTIONS_BY_CATEGORY,
} from 'stores/browseMotionStore';

import { BROWSE_DESKTOP_MIN_WIDTH } from '../constants';
import { useLibraryStore } from '../useLibraryStore';
import BrowseHeartButton from './BrowseHeartButton';
import BrowseMotionFeedbackMenu from './BrowseMotionFeedbackMenu';
import BrowseSearchResults from './BrowseSearchResults';
import InternalReviewIcons from './InternalReviewIcons';
import ReviewBrowseMotion from './ReviewBrowseMotion';

export type ModalStatus = 'searching' | 'trimming';

const titleCase = (string: string) =>
  string
    .split(' ')
    .map((word) => {
      return word === 'and'
        ? word
        : word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');

export default function BrowseMotionContent({
  close,
  prefillSearchTerm,
  browseItemJobId,
}: {
  close: () => void;
  prefillSearchTerm?: string;
  browseItemJobId?: string;
}) {
  const { isMobile } = useBreakpoint(BROWSE_DESKTOP_MIN_WIDTH);
  const { isCartwheeler } = useSnapshot(userStore.state);
  const { favoritePages, pages } = useSnapshot(browseMotionStore.state);
  const { addToFlagged, footContacts } = useLibraryStore();

  const [activeTags, setActiveTags] = useState<string[]>([]);
  const [chosenBrowseItemId, setChosenBrowseItemId] = useState<string | null>(
    browseItemJobId ?? null,
  );
  const [searchTerm, setSearchTerm] = useState(prefillSearchTerm || '');
  const [category, setCategory] = useState(
    isMobile ? '' : 'action and adventure',
  );
  const [sortBy, setSortBy] = useState<'Newest' | 'Oldest'>('Newest');
  const [subcategory, setSubcategory] = useState(isMobile ? '' : 'featured');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(),
  );
  const [categoryAndSubcategoryHash, setCategoryAndSubcategoryHash] = useState(
    `${category}.${subcategory}`,
  );

  const chosenBrowseItem = useMemo(() => {
    const allPages = [...favoritePages, ...pages];
    return chosenBrowseItemId
      ? allPages
          .flatMap((page) => page.items)
          .find((item) => item.jobID === chosenBrowseItemId)
      : null;
  }, [chosenBrowseItemId, favoritePages, pages]);

  useEffect(() => {
    if (category && subcategory) {
      setCategoryAndSubcategoryHash(`${category}.${subcategory}`);
    } else {
      setCategoryAndSubcategoryHash('');
    }
  }, [category, subcategory]);

  const {
    availableCategoryInformation: rawCategoryInformation,
    loading,
    selectedView,
    total,
    reviewMode,
  } = useSnapshot(browseMotionStore.state, { sync: true });

  // Memoize the heavy category computation
  const {
    tagsByCategoryAndSubcategory,
    totalByCategoryAndSubcategory,
    availableCategoryInformation,
  } = useMemo(() => {
    const tagsByCategoryAndSubcategory: Record<string, TagInfo[]> = { '': [] };
    const totalByCategoryAndSubcategory: Record<string, number> = {};
    const availableCategoryInformation: CategoryAggregation[] =
      rawCategoryInformation.map((category) => ({
        ...category,
        subcategories: (
          category.subcategories.map((subcategory) => {
            const catAndSubcat = `${category.category}.${subcategory.subcategory}`;
            totalByCategoryAndSubcategory[catAndSubcat] = subcategory.doc_count;
            const tagInfo = subcategory.tags as TagInfo[];
            tagsByCategoryAndSubcategory[catAndSubcat] = tagInfo;

            const existingTags = tagsByCategoryAndSubcategory[''];
            tagInfo.forEach((tag) => {
              if (existingTags.some((t) => t.tag === tag.tag)) {
                const existingIndex = existingTags.indexOf(tag);
                if (existingIndex >= 0) {
                  existingTags[existingIndex] = {
                    tag: tag.tag,
                    doc_count:
                      tag.doc_count + existingTags[existingIndex].doc_count,
                  };
                }
              } else {
                existingTags.push(tag);
              }
            });
            existingTags.sort((a, b) => b.doc_count - a.doc_count);
            return {
              ...subcategory,
              tags: tagInfo,
            };
          }) as SubcategoryInfo[]
        ).sort((subcat1, subcat2) =>
          subcat1.subcategory === 'featured'
            ? -1
            : subcat2.subcategory === 'featured'
              ? 1
              : 0,
        ),
      }));

    return {
      tagsByCategoryAndSubcategory,
      totalByCategoryAndSubcategory,
      availableCategoryInformation,
    };
  }, [rawCategoryInformation]); // Only recompute when rawCategoryInformation changes

  const currentTotal =
    category &&
    subcategory &&
    totalByCategoryAndSubcategory[categoryAndSubcategoryHash] > 0
      ? totalByCategoryAndSubcategory[categoryAndSubcategoryHash]
      : total;
  const truncatedText = useTruncateText(chosenBrowseItem?.short ?? '');
  const abortControllerRef = useRef<AbortController | null>(null);

  const cleanupSearch = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  // Memoize the debounced function so it persists across renders
  const debouncedSearch = useRef(
    debounce(
      (params: {
        searchTerm: string;
        category: string;
        subcategory: string;
      }) => {
        cleanupSearch();
        abortControllerRef.current = new AbortController();
        browseMotionStore.setCurrentSearchTerm(params.searchTerm);
        browseMotionStore.initSearch(
          {
            ...params,
          },
          abortControllerRef.current.signal,
        );
      },
      300,
    ),
  ).current;

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupSearch();
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    if (term.length) {
      debouncedSearch({ searchTerm: term, category: '', subcategory: '' });
    } else {
      debouncedSearch({ searchTerm: term, category, subcategory });
    }
    setActiveTags([]);
    // setChosenBrowseItem(null);
    if (term && selectedView === 'library') {
      setCategory('');
      setSubcategory('');
      setExpandedCategories(new Set());
    }
    if (term && selectedView === 'favorites') {
      setCategory('Favorites');
      setSubcategory('All Favorites');
      setExpandedCategories(new Set(['Favorites']));
    }
  };

  // This is a hack to prevent the view change from being called on the first mount
  const isFirstMount = useRef(true);
  useEffect(() => {
    handleViewChange(selectedView, isFirstMount.current);
    isFirstMount.current = false;
  }, [selectedView]);

  useEffect(() => {
    if (prefillSearchTerm) {
      handleSearch(prefillSearchTerm);
    }
  }, [prefillSearchTerm]);

  const handleSearchWithCategories = (
    term: string,
    category: string,
    subcategory: string,
  ) => {
    setSearchTerm(term);
    debouncedSearch({ searchTerm: term, category, subcategory });
  };

  const handleBackArrowClick = () => {
    setChosenBrowseItemId(null);
    setCategory('');
    setSubcategory('');
    setExpandedCategories(new Set());
  };

  const handleOuterCategoryClick = (cat: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(cat)) {
      newExpanded.delete(cat);
    } else {
      newExpanded.add(cat);
    }
    setExpandedCategories(newExpanded);
  };

  const handleOuterSubcategoryClick = (
    category: string,
    subcategory: string,
  ) => {
    const newExpanded = new Set(expandedCategories);
    newExpanded.add(category);

    setActiveTags([]);
    setChosenBrowseItemId(null);
    setExpandedCategories(newExpanded);
    setCategory(category);
    setSubcategory(subcategory);
    if (!searchTerm) {
      browseMotionStore.setLoading('data', true);
      handleSearchWithCategories(searchTerm, category, subcategory);
    }
  };

  const handleSuggestions = async () => {
    if (!searchTerm) return;
    await getBrowseSearchSuggestions(searchTerm);
  };

  const handleViewChange = (
    view: 'library' | 'favorites',
    isFirstMount?: boolean,
  ) => {
    browseMotionStore.setSelectedView(view);
    if (view === 'library') {
      handleSearch(searchTerm);
    }
    // Reset category and search state when switching views
    setCategory(
      view === 'library'
        ? isMobile
          ? ''
          : 'action and adventure'
        : 'Favorites',
    );
    setSubcategory(
      view === 'library' ? (isMobile ? '' : 'featured') : 'All Favorites',
    );
    setExpandedCategories(
      view === 'library' ? new Set() : new Set(['Favorites']),
    );
    setSearchTerm('');
    setActiveTags([]);
    if (isFirstMount !== true) {
      setChosenBrowseItemId(null);
    }
  };

  useEffect(() => {
    // Cleanup on unmount (ie. closing the motion library)
    return () => {
      browseMotionStore.setSelectedView('library');
    };
  }, []);

  if (isMobile) {
    return (
      <div className="flex h-full flex-col overflow-hidden">
        <HeaderSection
          handleSearch={handleSearch}
          handleSuggestions={handleSuggestions}
          handleViewChange={handleViewChange}
          isMobile={isMobile}
          loading={loading.data}
          searchTerm={searchTerm}
        />

        {/* Main content area - will scroll independently */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {currentTotal > 0 && chosenBrowseItem && (
            <div className="flex h-full flex-col">
              <div className="flex items-start justify-between p-4">
                <div className="flex items-start gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<ChevronLeftIcon />}
                    onClick={() => {
                      setChosenBrowseItemId(null);
                      setCategory('');
                      setSubcategory('');
                      setExpandedCategories(new Set());
                    }}
                  />
                  <div className="flex flex-col">
                    <Text className="pb-2" variant="studio/body/sm">
                      {chosenBrowseItem.title}
                    </Text>
                    <Text variant="studio/body/sm">{truncatedText}</Text>
                  </div>
                </div>
                <div className="flex items-center">
                  <BrowseHeartButton
                    className="border"
                    jobID={chosenBrowseItem.jobID}
                    title={chosenBrowseItem.title}
                    showNotification={true}
                  />
                  <BrowseMotionFeedbackMenu
                    video={chosenBrowseItem}
                    onBrowseChoice={(browseItem) => {
                      setChosenBrowseItemId(browseItem.jobID);
                    }}
                  />
                </div>
              </div>

              <div className="flex-1">
                <ReviewBrowseMotion
                  browseItem={chosenBrowseItem}
                  onAddMotion={
                    reviewMode ? () => setChosenBrowseItemId(null) : close
                  }
                />
              </div>
            </div>
          )}

          {currentTotal > 0 && !chosenBrowseItem && (
            <div className="flex h-full flex-col overflow-hidden">
              <div className="flex-1 overflow-auto">
                <div className="grow-0">
                  {!searchTerm && (
                    <CategoryList
                      categories={availableCategoryInformation}
                      category={category}
                      selectedCategories={expandedCategories}
                      selectedSubcategory={subcategory}
                      onBackClick={handleBackArrowClick}
                      onCategoryClick={handleOuterCategoryClick}
                      onSubcategoryClick={handleOuterSubcategoryClick}
                    />
                  )}
                </div>

                {(subcategory || searchTerm) && (
                  <>
                    <div className="shrink-0 border-b p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          {loading.data ? (
                            <Text
                              className="opacity-50"
                              variant="studio/body/sm"
                            >
                              Loading...
                            </Text>
                          ) : (
                            <>
                              <Text
                                className="opacity-50"
                                variant="studio/body/sm"
                              >
                                {currentTotal} motions in{' '}
                              </Text>
                              <Text variant="studio/body/medium/sm">
                                {subcategory
                                  ? `${category}/${subcategory}`
                                  : searchTerm}
                              </Text>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <BrowseSearchResults
                      activeTags={activeTags}
                      category={category}
                      onBrowseChoice={(browseItem) => {
                        setChosenBrowseItemId(browseItem.jobID);
                      }}
                      subcategory={subcategory}
                    />
                  </>
                )}
              </div>
            </div>
          )}

          {currentTotal == 0 && (
            <div className="flex-1 overflow-auto">
              <div className="shrink-0 border-b p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Text variant="studio/body/medium/sm">
                      No motions found for{' '}
                      <Text variant="studio/body/bold/md">{searchTerm}</Text>,
                      try searching again
                    </Text>
                  </div>
                </div>
              </div>

              <CategoryList
                categories={availableCategoryInformation}
                category={category}
                selectedCategories={expandedCategories}
                selectedSubcategory={subcategory}
                onBackClick={handleBackArrowClick}
                onCategoryClick={handleOuterCategoryClick}
                onSubcategoryClick={handleOuterSubcategoryClick}
              />
            </div>
          )}
        </div>

        {/* Footer - always fixed at bottom */}
        <div className="border-gray-30 dark:border-gray-60 mt-auto flex justify-between border-t p-4">
          <Button variant="tertiary" onClick={close}>
            Close
          </Button>
          {subcategory || searchTerm.length > 0 ? (
            <SortDropdown
              activeTags={activeTags}
              setActiveTags={setActiveTags}
              setSortBy={setSortBy}
              sortBy={sortBy}
              tagOptions={
                tagsByCategoryAndSubcategory[categoryAndSubcategoryHash]
              }
            />
          ) : (
            <div></div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex min-h-0 flex-1">
        <div className="border-gray-30 dark:border-gray-60 flex w-[330px] flex-none flex-col border-r">
          <HeaderSection
            handleSearch={handleSearch}
            handleSuggestions={handleSuggestions}
            handleViewChange={handleViewChange}
            isMobile={isMobile}
            loading={loading.data}
            searchTerm={searchTerm}
          />

          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            <CategoryList
              categories={availableCategoryInformation}
              category={category}
              selectedCategories={expandedCategories}
              selectedSubcategory={subcategory}
              onBackClick={handleBackArrowClick}
              onCategoryClick={handleOuterCategoryClick}
              onSubcategoryClick={handleOuterSubcategoryClick}
            />
          </div>
        </div>

        <div className="flex min-w-0 flex-1 flex-col">
          {currentTotal > 0 && chosenBrowseItem && (
            <div className="flex h-full flex-col">
              <div className="border-gray-30 dark:border-gray-60 flex items-start justify-between border-b p-4">
                <div className="flex min-w-0 items-center gap-4">
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<ChevronLeftIcon />}
                    onClick={() => {
                      setChosenBrowseItemId(null);
                    }}
                  />
                  <Text as="h2" variant="studio/title/md" className="truncate">
                    {chosenBrowseItem.title}
                  </Text>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <BrowseHeartButton
                    className="border"
                    jobID={chosenBrowseItem.jobID}
                    title={chosenBrowseItem.title}
                    showNotification={true}
                  />
                  {isCartwheeler && (
                    <InternalReviewIcons
                      video={chosenBrowseItem}
                      category={category}
                      subcategory={subcategory}
                      addToFlagged={addToFlagged}
                    />
                  )}
                  <BrowseMotionFeedbackMenu
                    video={chosenBrowseItem}
                    onBrowseChoice={(browseItem) => {
                      setChosenBrowseItemId(browseItem.jobID);
                    }}
                  />
                </div>
              </div>
              <div className="flex flex-col p-4 pb-0">
                <Text variant="studio/body/medium/sm">{truncatedText}</Text>

                {chosenBrowseItem.tags.length > 0 && (
                  <div className="text-gray-60 dark:text-gray-45">
                    <Text variant="studio/body/medium/sm">Tags: </Text>

                    <Text variant="studio/body/medium/sm">
                      {chosenBrowseItem.tags.join(', ')}
                    </Text>
                  </div>
                )}
              </div>
              <div className="min-h-0 flex-1 overflow-auto scroll-smooth">
                <ReviewBrowseMotion
                  browseItem={chosenBrowseItem}
                  onAddMotion={
                    reviewMode ? () => setChosenBrowseItemId(null) : close
                  }
                />
              </div>
            </div>
          )}
          {currentTotal > 0 && !chosenBrowseItem && (
            <div className="flex h-full flex-col">
              <div className="border-gray-30 dark:border-gray-60 shrink-0 border-b p-4">
                <div className="flex items-center justify-between">
                  <div>
                    {loading.data ? (
                      <Text className="opacity-50" variant="studio/body/sm">
                        Loading...
                      </Text>
                    ) : (
                      <>
                        <Text className="opacity-50" variant="studio/body/sm">
                          {currentTotal} motions in{' '}
                        </Text>
                        <Text variant="studio/body/medium/sm">
                          {subcategory
                            ? `${category}/${subcategory}`
                            : searchTerm
                              ? searchTerm
                              : 'Library'}
                        </Text>
                      </>
                    )}
                  </div>

                  <SortDropdown
                    activeTags={activeTags}
                    setActiveTags={setActiveTags}
                    sortBy={sortBy}
                    setSortBy={setSortBy}
                    tagOptions={
                      tagsByCategoryAndSubcategory[categoryAndSubcategoryHash]
                    }
                  />
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-auto">
                <BrowseSearchResults
                  activeTags={activeTags}
                  category={category}
                  onBrowseChoice={(browseItem) => {
                    setChosenBrowseItemId(browseItem.jobID);
                  }}
                  subcategory={subcategory}
                />
              </div>
            </div>
          )}
          {currentTotal === 0 && searchTerm && (
            <div className="flex h-full flex-col">
              <div className="shrink-0 border-b p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Text variant="studio/body/medium/sm">
                      No motions found for{' '}
                      <Text variant="studio/body/bold/md">{searchTerm}</Text>,
                      try searching again
                    </Text>
                  </div>

                  <SortDropdown
                    activeTags={activeTags}
                    setActiveTags={setActiveTags}
                    sortBy={sortBy}
                    setSortBy={setSortBy}
                    tagOptions={
                      tagsByCategoryAndSubcategory[categoryAndSubcategoryHash]
                    }
                  />
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-auto">
                <BrowseSearchResults
                  activeTags={activeTags}
                  category={category}
                  onBrowseChoice={(browseItem) => {
                    setChosenBrowseItemId(browseItem.jobID);
                  }}
                  subcategory={subcategory}
                />
              </div>
            </div>
          )}
          {/* Footer - always fixed at bottom */}
          <div className="border-gray-30 dark:border-gray-60 flex justify-end border-t p-4">
            <Button variant="tertiary" onClick={close}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CategoryList({
  categories,
  category,
  selectedCategories,
  selectedSubcategory,
  onBackClick,
  onCategoryClick,
  onSubcategoryClick,
}: {
  categories: readonly CategoryAggregation[];
  category: string;
  selectedCategories: Set<string>;
  selectedSubcategory: string;
  onBackClick: () => void;
  onCategoryClick: (category: string) => void;
  onSubcategoryClick: (category: string, subcategory: string) => void;
}) {
  const baseURL = getResourcesUrl(false);
  const { isMobile } = useBreakpoint(BROWSE_DESKTOP_MIN_WIDTH);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(
    selectedCategories.size ? Array.from(selectedCategories)[0] : null,
  );
  const activeCategoryAndSubcategory = `${category}.${selectedSubcategory}`;

  const handleCategoryClick = (cat: string) => {
    if (isMobile) {
      setExpandedCategory(expandedCategory === cat ? null : cat);
      onSubcategoryClick(
        cat,
        categories.find((val) => val.category === cat)?.subcategories[0]
          .subcategory ?? '',
      );
    } else {
      onCategoryClick(cat);
    }
  };

  if (isMobile && expandedCategory) {
    return (
      <div className="flex h-full flex-col">
        <div
          className="flex items-center gap-2 p-4"
          onClick={() => {
            onBackClick();
            setExpandedCategory(null);
          }}
        >
          <ChevronLeftIcon />
          <Text variant="studio/body/medium/sm">
            {titleCase(expandedCategory)}
          </Text>
        </div>
        <div className="mx-1 overflow-x-auto scroll-smooth pr-4">
          <div className="flex gap-2">
            {categories
              .find((val) => val.category === expandedCategory)
              ?.subcategories.map((subCategoryInfo) => (
                <Button
                  variant="secondary"
                  className={`whitespace-nowrap ${
                    selectedSubcategory === subCategoryInfo.subcategory
                      ? ''
                      : 'opacity-50'
                  }`}
                  key={subCategoryInfo.subcategory}
                  onClick={() =>
                    onSubcategoryClick(
                      expandedCategory,
                      subCategoryInfo.subcategory,
                    )
                  }
                >
                  {titleCase(subCategoryInfo.subcategory)}
                </Button>
              ))}
          </div>
        </div>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="grid grid-cols-2 gap-4 p-4">
        {categories.map((categoryInfo) => (
          <div
            key={categoryInfo.category}
            onClick={() => handleCategoryClick(categoryInfo.category)}
            className="flex cursor-pointer flex-col rounded-lg"
          >
            <div className="overflow-hidden rounded-lg">
              <img
                src={`${baseURL}${
                  IMAGE_PATH_BY_CATEGORY[categoryInfo.category]
                }`}
                alt={titleCase(categoryInfo.category)}
                className="h-full w-full object-cover"
              />
            </div>
            <div className="mt-2 flex flex-col">
              <Text variant="studio/body/medium/sm">
                {titleCase(categoryInfo.category)}
              </Text>
              <Text
                variant="studio/body/medium/sm"
                className={`text-gray-60 dark:text-gray-30 cursor-pointer pb-2`}
              >
                {MOTIONS_BY_CATEGORY[categoryInfo.category]} motions
              </Text>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      <Text className="p-4" variant="studio/body/medium/sm">
        Categories
      </Text>
      <div className="flex-1 overflow-y-auto px-4">
        <ul className="flex-1 overflow-y-auto px-4">
          {categories.map((categoryInfo) => (
            <li key={categoryInfo.category}>
              <div
                className={`${
                  selectedCategories.has(categoryInfo.category)
                    ? 'bg-gray-15 dark:bg-gray-75'
                    : ''
                } flex cursor-pointer items-center rounded-lg p-2`}
                onClick={() => onCategoryClick(categoryInfo.category)}
              >
                <span className="mr-2 opacity-75">
                  {selectedCategories.has(categoryInfo.category) ? (
                    <ChevronDropdownIcon />
                  ) : (
                    <ChevronRightIcon />
                  )}
                </span>
                <Text variant="studio/body/medium/sm">
                  {titleCase(categoryInfo.category)}
                </Text>
              </div>
              {selectedCategories.has(categoryInfo.category) && (
                <ul className="mt-1 ml-6 space-y-1">
                  {categories
                    .find((val) => val.category === categoryInfo.category)
                    ?.subcategories.map((subCategoryInfo) => (
                      <li
                        key={subCategoryInfo.subcategory}
                        onClick={() =>
                          onSubcategoryClick(
                            categoryInfo.category,
                            subCategoryInfo.subcategory,
                          )
                        }
                        className={`cursor-pointer rounded-lg p-2 ${
                          activeCategoryAndSubcategory ===
                          `${categoryInfo.category}.${subCategoryInfo.subcategory}`
                            ? 'bg-blue text-white'
                            : ''
                        }`}
                      >
                        <Text variant="studio/body/medium/sm">
                          {titleCase(subCategoryInfo.subcategory)}
                        </Text>
                      </li>
                    ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

function HeaderSection({
  handleSearch,
  handleSuggestions,
  handleViewChange,
  isMobile,
  loading,
  searchTerm,
}: {
  handleSearch: (term: string) => void;
  handleSuggestions: () => void;
  handleViewChange: (view: 'library' | 'favorites') => void;
  isMobile: boolean;
  loading: boolean;
  searchTerm: string;
}) {
  const { selectedView, reviewMode } = useSnapshot(browseMotionStore.state);
  const { isDataLabeler } = useSnapshot(userStore.state);
  const showReviewMode = isDataLabeler;
  const { footContacts } = useLibraryStore();

  const handleReviewModeToggle = (checked: boolean) => {
    browseMotionStore.setReviewMode(checked);
  };

  return (
    <div className="border-gray-30 dark:border-gray-60 flex-none border-b p-4">
      {!isMobile && (
        <>
          <RxDialog.Title asChild>
            <Text as="h2" variant="studio/title/lg" className="mb-2">
              Motion Library
            </Text>
          </RxDialog.Title>

          <RxDialog.Description asChild>
            <Text
              as="p"
              variant="studio/body/sm"
              className="text-gray-60 dark:text-gray-45 mb-2"
            >
              Browse motions to add and sequence with your current animation.
            </Text>
          </RxDialog.Description>
        </>
      )}
      {isMobile && (
        <SearchBar
          className="mb-2"
          value={searchTerm}
          onChange={handleSearch}
          onBlur={handleSuggestions}
          loading={loading}
          selectedView={selectedView}
        />
      )}
      <div className="flex w-full justify-between gap-2">
        <Button
          className={`w-full ${selectedView === 'library' ? '' : 'opacity-50'}`}
          onClick={() => handleViewChange('library')}
        >
          Cartwheel Library
        </Button>
        <Button
          className={`w-full ${
            selectedView === 'favorites' ? '' : 'opacity-50'
          }`}
          onClick={() => handleViewChange('favorites')}
        >
          My Favorites
        </Button>
      </div>

      {!isMobile && (
        <SearchBar
          className="mt-2 mb-2"
          value={searchTerm}
          onChange={handleSearch}
          onBlur={handleSuggestions}
          loading={loading}
          selectedView={selectedView}
        />
      )}

      {showReviewMode && (
        <div className="mt-2 flex items-center">
          <Checkbox
            id="review-mode-toggle"
            checked={reviewMode}
            setChecked={handleReviewModeToggle}
          />
          <label htmlFor="review-mode-toggle" className="ml-2 cursor-pointer">
            <Text variant="studio/body/sm">
              Review Mode ({footContacts.size})
            </Text>
          </label>
        </div>
      )}
    </div>
  );
}

function SearchBar({
  className,
  value,
  onChange,
  onBlur,
  loading,
  selectedView,
}: {
  className?: string;
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  loading: boolean;
  selectedView: 'library' | 'favorites';
}) {
  return (
    <div className={`relative ${className}`}>
      <div tabIndex={0} id="dummy-focus-trap" />
      <SearchIcon className="pointer-events-none absolute top-1/2 left-2 -translate-y-1/2" />
      <Input
        name="Searcher"
        value={value}
        placeholder={
          selectedView === 'favorites'
            ? 'Search your favorites'
            : 'Search Cartwheel library'
        }
        className={`pl-8 ${loading ? 'pr-8' : ''}`}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
      />
      {loading && (
        <div className="absolute top-1/2 right-2 -translate-y-1/2">
          <div className="border-t-blue h-4 w-4 animate-spin rounded-full border-2" />
        </div>
      )}
    </div>
  );
}

function SortDropdown({
  activeTags,
  setActiveTags,
  setSortBy,
  sortBy,
  tagOptions,
}: {
  activeTags: string[];
  setActiveTags: (tags: string[]) => void;
  setSortBy: (value: 'Newest' | 'Oldest') => void;
  sortBy: 'Newest' | 'Oldest';
  tagOptions?: TagInfo[];
}) {
  // tagOptions are sorted originally by the # of motions per tag, but to display
  // to the user we want to show them the tags alphabetically, keeping only the top 20
  // tags before sorting alphabetically
  const MAX_TAGS = 20;
  const displayedTags = tagOptions?.slice(0, MAX_TAGS).sort((tag1, tag2) => {
    return tag1.tag < tag2.tag ? -1 : 1;
  });
  return (
    <div className="flex items-center">
      <Dropdown
        trigger={
          <Button variant="secondary" size="sm" icon={<SortIcon />}>
            Filter/Sort
          </Button>
        }
      >
        <Text as="div" variant="studio/body/sm" className="px-4 py-2">
          Filter by tag
        </Text>
        <div className="flex max-w-[400px] flex-wrap gap-2 pb-4 pl-4">
          {displayedTags?.map((tagInfo) => (
            // These are purposefully not Dropdown.Item because
            // we want the Dropdown to remain open when they are clicked
            <Checkbox
              className="text-gray-90 dark:text-gray-15 rounded-lg border p-1 pr-2"
              checked={activeTags.includes(tagInfo.tag)}
              color="settings"
              id={tagInfo.tag}
              key={tagInfo.tag}
              label={`${tagInfo.tag.charAt(0).toUpperCase()}${tagInfo.tag.slice(
                1,
              )}`}
              labelSecondary={`(${tagInfo.doc_count})`}
              setChecked={() => {
                if (activeTags.includes(tagInfo.tag)) {
                  setActiveTags(activeTags.filter((t) => t !== tagInfo.tag));
                } else {
                  setActiveTags([...activeTags, tagInfo.tag]);
                }
              }}
              variant="checkbox"
            />
          ))}
          {(displayedTags === undefined || displayedTags.length === 0) && (
            <Text
              className="text-gray-60 dark:text-gray-30"
              as="div"
              variant="studio/body/sm"
            >
              No tags to filter by
            </Text>
          )}
        </div>
        <Text
          as="div"
          variant="studio/body/sm"
          className="border-gray-30 dark:border-gray-60 border-t px-4 pt-4"
        >
          Sort by
        </Text>
        <Dropdown.Item>
          <Checkbox
            checked={sortBy === 'Newest'}
            id="Newest"
            label="Newest"
            setChecked={(checked) => {
              if (checked) {
                setSortBy('Newest');
              }
            }}
            variant="radio"
          />
        </Dropdown.Item>
        <Dropdown.Item>
          <Checkbox
            checked={sortBy === 'Oldest'}
            id="Oldest"
            label="Oldest"
            setChecked={(checked) => {
              if (checked) {
                setSortBy('Oldest');
              }
            }}
            variant="radio"
          />
        </Dropdown.Item>
      </Dropdown>
    </div>
  );
}
